/**
 * useProjectCache Hook
 * Extracted from MultiProjectContext.tsx
 * Handles project cache management and WebSocket synchronization
 */

import { useCallback } from 'react';
import { ProjectData, type VisibilityTier } from '@/shared/types/projectData';
import { log } from '@/shared/utils/logger';
import { sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import { getNodeId } from '../utils/projectUtils';
import type { ProjectDataWithMeta } from '../types/multiProject';
import type { GitHubConfig, ProjectInfo } from '@/features/projects/services/projectService';

interface UseProjectCacheParams {
  projectsData: Map<string, ProjectDataWithMeta>;
  visibleProjectIds: Set<string>;
  setProjectsData: (updater: (prev: Map<string, ProjectDataWithMeta>) => Map<string, ProjectDataWithMeta>) => void;
  setAvailableProjects: (updater: (prev: ProjectInfo[]) => ProjectInfo[]) => void;
}

export function useProjectCache({
  projectsData,
  visibleProjectIds,
  setProjectsData,
  setAvailableProjects
}: UseProjectCacheParams) {
  type CacheProjectUpdate = Partial<ProjectData> & {
    project_name?: string;
    description?: string;
    company_name?: string;
    status?: ProjectInfo['status'];
    githubConfig?: GitHubConfig;
    github_config?: GitHubConfig;
    visibilityTierDefault?: VisibilityTier;
    visibility_tier_default?: VisibilityTier;
    projectAssigneeId?: string | null;
    project_assignee_id?: string | null;
    leadAgentProfileId?: string | null;
    lead_agent_profile_id?: string | null;
    projectType?: ProjectData['metadata']['settings']['projectType'];
    project_type?: ProjectData['metadata']['settings']['projectType'];
    workflowStatus?: ProjectData['metadata']['settings']['workflowStatus'];
    workflow_status?: ProjectData['metadata']['settings']['workflowStatus'];
    projectSlug?: string;
    project_slug?: string;
  };

  const isCached = useCallback((projectId: string) => {
    return projectsData.has(projectId);
  }, [projectsData]);

  const isVisible = useCallback((projectId: string) => {
    return visibleProjectIds.has(projectId);
  }, [visibleProjectIds]);

  const updateProjectInCache = useCallback((projectId: string, updatedData: Partial<ProjectData>) => {
    log.debug('MultiProject', '🔵 [CACHE UPDATE] updateProjectInCache called for project:', projectId);
    log.debug('MultiProject', '🔵 [CACHE UPDATE] Update contains nodes:', updatedData.nodes?.length || 0);

    // CRITICAL FIX: Update synchronously to prevent 0-node flicker
    // This function is only called from WebSocket handlers (async event handlers),
    // never from render functions, so synchronous updates are safe and prevent
    // the brief 0-node state that was causing order changes
    log.debug('MultiProject', '🟢 [CACHE UPDATE] Performing SYNCHRONOUS update for project:', projectId);

    setProjectsData(prev => {
      try {
        const newMap = new Map(prev);
        const existingData = newMap.get(projectId);

        if (!existingData) {
          log.warn('MultiProject', '🔍 [MULTI-PROJECT DEBUG] Project not in cache:', projectId);
          return prev;
        }

        log.debug('MultiProject', '🟡 [CACHE UPDATE] Found existing data for project:', projectId);

        // Extract metadata updates
        const innerDataWithLegacy = updatedData as CacheProjectUpdate;
        const incomingProjectName = innerDataWithLegacy.projectName || innerDataWithLegacy.project_name;
        const incomingDescription = innerDataWithLegacy.description;
        // A present key with an empty value is an explicit clear (Company/Client removed in
        // Project Settings) and must overwrite the cached label; an absent key leaves it alone.
        const hasIncomingClient = Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'client');
        const incomingClient = hasIncomingClient ? (innerDataWithLegacy.client ?? undefined) : undefined;
        const hasIncomingCompanyName =
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'companyName') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'company_name');
        const incomingCompanyName = hasIncomingCompanyName
          ? (innerDataWithLegacy.companyName ?? innerDataWithLegacy.company_name ?? undefined)
          : undefined;
        const incomingStatus = innerDataWithLegacy.status;
        const incomingGithubConfig = innerDataWithLegacy.githubConfig || innerDataWithLegacy.github_config;
        const incomingProjectSlug = innerDataWithLegacy.projectSlug || innerDataWithLegacy.project_slug;
        const incomingVisibilityTierDefault =
          innerDataWithLegacy.visibilityTierDefault ||
          innerDataWithLegacy.visibility_tier_default ||
          innerDataWithLegacy.metadata?.settings?.visibilityTierDefault;
        const hasIncomingProjectAssignee =
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'projectAssigneeId') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'project_assignee_id') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy.metadata?.settings || {}, 'projectAssigneeId');
        const incomingProjectAssigneeId = hasIncomingProjectAssignee
          ? (
              innerDataWithLegacy.projectAssigneeId ??
              innerDataWithLegacy.project_assignee_id ??
              innerDataWithLegacy.metadata?.settings?.projectAssigneeId ??
              null
            )
          : undefined;
        const hasIncomingLeadAgent =
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'leadAgentProfileId') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'lead_agent_profile_id') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy.metadata?.settings || {}, 'leadAgentProfileId');
        const incomingLeadAgentProfileId = hasIncomingLeadAgent
          ? (
              innerDataWithLegacy.leadAgentProfileId ??
              innerDataWithLegacy.lead_agent_profile_id ??
              innerDataWithLegacy.metadata?.settings?.leadAgentProfileId ??
              null
            )
          : undefined;
        const hasIncomingProjectType =
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'projectType') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'project_type') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy.metadata?.settings || {}, 'projectType');
        const incomingProjectType = hasIncomingProjectType
          ? (
              innerDataWithLegacy.projectType ??
              innerDataWithLegacy.project_type ??
              innerDataWithLegacy.metadata?.settings?.projectType ??
              null
            )
          : undefined;
        const hasIncomingWorkflowStatus =
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'workflowStatus') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy, 'workflow_status') ||
          Object.prototype.hasOwnProperty.call(innerDataWithLegacy.metadata?.settings || {}, 'workflowStatus');
        const incomingWorkflowStatus = hasIncomingWorkflowStatus
          ? (
              innerDataWithLegacy.workflowStatus ??
              innerDataWithLegacy.workflow_status ??
              innerDataWithLegacy.metadata?.settings?.workflowStatus
            )
          : undefined;

        // Incoming `nodes` are authoritative full-project snapshots produced by:
        // - local optimistic operation application
        // - websocket-applied operation results
        // - explicit per-project next-state calculations
        // They must REPLACE the cached node array. Merging old nodes back in resurrects
        // deleted items and causes the UI to look stale until a hard refresh.
        let mergedNodes = existingData.nodes;
        if (Object.prototype.hasOwnProperty.call(updatedData, 'nodes') && Array.isArray(updatedData.nodes)) {
          const incomingNodes = updatedData.nodes;
          const incomingCount = incomingNodes.length;
          const existingCount = existingData.nodes.length;

          log.debug('MultiProject', '🔄 [CACHE UPDATE] MERGING nodes:', {
            existing: existingCount,
            incoming: incomingCount,
            ratio: (incomingCount / existingCount * 100).toFixed(1) + '%'
          });

          // CRITICAL FIX: Strip project ID prefix from node IDs for cache storage
          // Cache stores nodes per-project, so IDs should not have projectId: prefix
          // But incoming nodes from WebSocket may have been normalized with projectId: prefix
          const stripProjectPrefix = (id: string) => {
            return getNodeId(id);
          };

          // Strip prefixes from incoming nodes before caching
          mergedNodes = incomingNodes.map(incomingNode => ({
            ...incomingNode,
            id: stripProjectPrefix(incomingNode.id),
            parentId: incomingNode.parentId ? stripProjectPrefix(incomingNode.parentId) : incomingNode.parentId,
            dependencies: Array.isArray(incomingNode.dependencies)
              ? incomingNode.dependencies.map(stripProjectPrefix)
              : incomingNode.dependencies
          }));

          log.debug('MultiProject', '✅ [CACHE UPDATE] Merged nodes count:', mergedNodes.length);
        }

        const nextMetadata = incomingVisibilityTierDefault || hasIncomingProjectAssignee || hasIncomingLeadAgent || hasIncomingProjectType || hasIncomingWorkflowStatus
          ? {
              ...existingData.metadata,
              settings: {
                ...existingData.metadata?.settings,
                ...(incomingVisibilityTierDefault ? { visibilityTierDefault: incomingVisibilityTierDefault } : {}),
                ...(hasIncomingProjectAssignee ? { projectAssigneeId: incomingProjectAssigneeId ?? null } : {}),
                ...(hasIncomingLeadAgent ? { leadAgentProfileId: incomingLeadAgentProfileId ?? null } : {}),
                ...(hasIncomingProjectType ? { projectType: incomingProjectType ?? null } : {}),
                ...(hasIncomingWorkflowStatus ? { workflowStatus: incomingWorkflowStatus } : {}),
              },
            }
          : existingData.metadata;

        const cacheUpdatedAt = new Date().toISOString();
        const updatedProject: ProjectDataWithMeta = {
          ...existingData,
          projectInfo: {
            ...existingData.projectInfo,
            ...(incomingProjectName ? { project_name: incomingProjectName, projectName: incomingProjectName } : {}),
            ...(incomingDescription !== undefined ? { description: incomingDescription } : {}),
            ...(hasIncomingClient ? { client: incomingClient } : {}),
            ...(hasIncomingCompanyName ? { companyName: incomingCompanyName } : {}),
            ...(incomingStatus !== undefined ? { status: incomingStatus } : {}),
            ...(incomingGithubConfig !== undefined ? { githubConfig: incomingGithubConfig } : {}),
            ...(incomingProjectSlug !== undefined ? { projectSlug: incomingProjectSlug } : {}),
            ...(hasIncomingProjectAssignee ? { projectAssigneeId: incomingProjectAssigneeId ?? null } : {}),
            ...(hasIncomingLeadAgent ? { leadAgentProfileId: incomingLeadAgentProfileId ?? null } : {}),
          },
          ...(incomingProjectName ? { projectName: incomingProjectName } : {}),
          ...(hasIncomingClient ? { client: incomingClient } : {}),
          ...(hasIncomingCompanyName ? { companyName: incomingCompanyName } : {}),
          ...(incomingDescription !== undefined ? { description: incomingDescription } : {}),
          ...(incomingStatus !== undefined ? { status: incomingStatus } : {}),
          ...(incomingGithubConfig !== undefined ? { githubConfig: incomingGithubConfig } : {}),
          ...(incomingProjectSlug !== undefined ? { projectSlug: incomingProjectSlug } : {}),
          ...(hasIncomingProjectAssignee ? { projectAssigneeId: incomingProjectAssigneeId ?? null } : {}),
          ...(hasIncomingLeadAgent ? { leadAgentProfileId: incomingLeadAgentProfileId ?? null } : {}),
          metadata: nextMetadata,
          ...(updatedData.kanbanColumns ? { kanbanColumns: updatedData.kanbanColumns } : {}),
          ...(Object.prototype.hasOwnProperty.call(updatedData, 'rootNodeIds')
            ? { rootNodeIds: updatedData.rootNodeIds || [] }
            : {}),
          nodes: mergedNodes,
          updatedAt: cacheUpdatedAt,
          lastSynced: new Date(),
        };

        const sanitizedProject = sanitizeProjectData(
          {
            version: updatedProject.version,
            projectId: updatedProject.projectId,
            projectName: updatedProject.projectName,
            client: updatedProject.client,
            projectAssigneeId: updatedProject.projectAssigneeId,
            leadAgentProfileId: updatedProject.leadAgentProfileId,
            nodes: updatedProject.nodes,
            rootNodeIds: updatedProject.rootNodeIds,
            kanbanColumns: updatedProject.kanbanColumns,
            files: updatedProject.files,
            fileAuditLogs: updatedProject.fileAuditLogs,
            createdAt: updatedProject.createdAt,
            updatedAt: updatedProject.updatedAt,
            metadata: updatedProject.metadata,
          },
          { logRepairs: false }
        );

        newMap.set(projectId, {
          ...updatedProject,
          nodes: sanitizedProject.nodes,
          rootNodeIds: sanitizedProject.rootNodeIds,
          kanbanColumns: sanitizedProject.kanbanColumns,
        });
        return newMap;
      } catch (e) {
        log.error('MultiProject', '❌ [CACHE UPDATE] Error updating cache; keeping previous state', e);
        return prev;
      }
    });

    const outerDataWithLegacy = updatedData as CacheProjectUpdate;
    const outerProjectName = outerDataWithLegacy.projectName || outerDataWithLegacy.project_name;
    const outerDescription = outerDataWithLegacy.description;
    const hasOuterClient = Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'client');
    const outerClient = hasOuterClient ? (outerDataWithLegacy.client ?? undefined) : undefined;
    const hasOuterCompanyName =
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'companyName') ||
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'company_name');
    const outerCompanyName = hasOuterCompanyName
      ? (outerDataWithLegacy.companyName ?? outerDataWithLegacy.company_name ?? undefined)
      : undefined;
    const outerStatus = outerDataWithLegacy.status;
    const outerGithubConfig = outerDataWithLegacy.githubConfig || outerDataWithLegacy.github_config;
    const outerProjectSlug = outerDataWithLegacy.projectSlug || outerDataWithLegacy.project_slug;
    const hasOuterProjectAssignee =
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'projectAssigneeId') ||
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'project_assignee_id') ||
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy.metadata?.settings || {}, 'projectAssigneeId');
    const outerProjectAssigneeId = hasOuterProjectAssignee
      ? (
          outerDataWithLegacy.projectAssigneeId ??
          outerDataWithLegacy.project_assignee_id ??
          outerDataWithLegacy.metadata?.settings?.projectAssigneeId ??
          null
        )
      : undefined;
    const hasOuterLeadAgent =
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'leadAgentProfileId') ||
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy, 'lead_agent_profile_id') ||
      Object.prototype.hasOwnProperty.call(outerDataWithLegacy.metadata?.settings || {}, 'leadAgentProfileId');
    const outerLeadAgentProfileId = hasOuterLeadAgent
      ? (
          outerDataWithLegacy.leadAgentProfileId ??
          outerDataWithLegacy.lead_agent_profile_id ??
          outerDataWithLegacy.metadata?.settings?.leadAgentProfileId ??
          null
        )
      : undefined;
    if (
      outerProjectName ||
      hasOuterClient ||
      outerDescription !== undefined ||
      hasOuterCompanyName ||
      outerStatus !== undefined ||
      outerGithubConfig !== undefined ||
      outerProjectSlug !== undefined ||
      hasOuterProjectAssignee ||
      hasOuterLeadAgent
    ) {
      setAvailableProjects(prev => prev.map(p => {
        if (p.projectId === projectId) {
          return {
            ...p,
            ...(outerProjectName ? { projectName: outerProjectName } : {}),
            ...(hasOuterClient ? { client: outerClient } : {}),
            ...(outerDescription !== undefined ? { description: outerDescription } : {}),
            ...(hasOuterCompanyName ? { companyName: outerCompanyName } : {}),
            ...(outerStatus !== undefined ? { status: outerStatus } : {}),
            ...(outerGithubConfig !== undefined ? { githubConfig: outerGithubConfig } : {}),
            ...(outerProjectSlug !== undefined ? { projectSlug: outerProjectSlug } : {}),
            ...(hasOuterProjectAssignee ? { projectAssigneeId: outerProjectAssigneeId ?? null } : {}),
            ...(hasOuterLeadAgent ? { leadAgentProfileId: outerLeadAgentProfileId ?? null } : {}),
          } as ProjectInfo;
        }
        return p;
      }));
    }
  }, [setProjectsData, setAvailableProjects]);

  return {
    isCached,
    isVisible,
    updateProjectInCache
  };
}
